import {
  PRODUCT_ORIGINS,
  PRODUCT_TYPES,
  PRODUCT_TYPE_LABEL,
  formatQuantity,
  packagingFactor,
  packagingInputSchema,
  productCreateSchema,
  productUpdateSchema,
  type CadastroOptions,
  type Packaging,
  type Product,
  type ProductType,
  type TaxGroup,
} from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";
import { notifySuccess } from "@/lib/notice";
import {
  issuesToErrors,
  str,
  useCadastroList,
  useCadastroOptions,
  useForm,
  useListState,
  useSave,
} from "./lib";
import { Checkbox, ListToolbar, Pager, SaveError, StatusBadge } from "./widgets";
import { useAutoCode } from "./auto-code";
import { CodeField } from "./CodeField";

type Unit = CadastroOptions["units"][number];
type ProductRow = Product & { packagingCount: number };

export function ProductsPage() {
  const canEdit = useCan()("cadastros.produtos.gerenciar");
  const ls = useListState();
  const [tipo, setTipo] = useState<ProductType | "">("");
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const list = useCadastroList<ProductRow>("produtos", {
    ...ls,
    ...(tipo ? { extra: { tipo } } : {}),
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Produtos"
        description="Produtos acabados, café cru, embalagens e insumos, com as embalagens de venda e compra."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Novo produto
            </Button>
          )
        }
      />
      {editing && (
        <ProductEditor
          key={editing}
          productId={editing === "new" ? null : editing}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
          onCreated={(id) => setEditing(id)}
        />
      )}
      <Card>
        <ListToolbar
          q={ls.q}
          onQ={ls.setQ}
          status={ls.status}
          onStatus={ls.setStatus}
          placeholder="Buscar por código, nome ou GTIN"
        >
          <Select
            className="w-56"
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as ProductType | "");
              ls.setOffset(0);
            }}
            aria-label="Tipo"
          >
            <option value="">Todos os tipos</option>
            {PRODUCT_TYPES.map((t) => (
              <option key={t} value={t}>
                {PRODUCT_TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </ListToolbar>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={["Código", "Nome", "Tipo", "Unidade-base", "NCM", "Embalagens", "Situação", ""]}
            >
              {list.data?.items.map((p) => (
                <tr key={p.id}>
                  <td className="px-3 py-2 font-mono">{p.code}</td>
                  <td className="px-3 py-2 font-medium">{p.name}</td>
                  <td className="px-3 py-2">{PRODUCT_TYPE_LABEL[p.type]}</td>
                  <td className="px-3 py-2 font-mono">{p.baseUnitCode}</td>
                  <td className="px-3 py-2 font-mono">{p.ncm ?? "—"}</td>
                  <td className="px-3 py-2">{p.packagingCount}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p.id)}>
                      {canEdit ? "Editar" : "Ver"}
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={ls.offset} onOffset={ls.setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ produto

function ProductEditor({
  productId,
  canEdit,
  onClose,
  onCreated,
}: {
  productId: string | null;
  canEdit: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const product = useQuery({
    queryKey: ["cadastros", "produtos", "detalhe", productId],
    queryFn: () => apiFetch<Product>(`/cadastros/produtos/${productId}`),
    enabled: productId !== null,
  });
  if (productId && product.isPending) return <Spinner />;
  if (productId && product.isError)
    return <Alert tone="error">{errorMessage(product.error)}</Alert>;
  return (
    <ProductForm
      key={product.data?.version ?? "new"}
      product={product.data ?? null}
      canEdit={canEdit}
      onClose={onClose}
      onCreated={onCreated}
    />
  );
}

/** Embalagem ainda não gravada (cadastro de produto novo). */
type DraftPackaging = {
  unitId: string;
  factor: string;
  containsQuantity: string;
  containsUnitId: string;
  gtin: string;
  description: string;
  isSaleUnit: boolean;
  isPurchaseUnit: boolean;
};

function ProductForm({
  product,
  canEdit,
  onClose,
  onCreated,
}: {
  product: Product | null;
  canEdit: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const options = useCadastroOptions();
  const can = useCan();
  // grupo tributário e EX TIPI mexem no cálculo fiscal: só quem prepara a matriz altera
  const canFiscal = can("fiscal.matriz.preparar");
  const groups = useQuery({
    queryKey: ["fiscal", "/grupos"],
    queryFn: () => apiFetch<TaxGroup[]>("/fiscal/grupos"),
    enabled: can("fiscal.ver"),
  });
  const units = options.data?.units ?? [];
  const f = useForm({
    code: str(product?.code),
    name: str(product?.name),
    description: str(product?.description),
    type: product?.type ?? "produto_acabado",
    baseUnitId: str(product?.baseUnitId),
    ncm: str(product?.ncm),
    cest: str(product?.cest),
    origin: product?.origin ?? "0",
    exTipi: str(product?.exTipi),
    taxGroupId: str(product?.taxGroupId),
    gtin: str(product?.gtin),
    netWeightKg: str(product?.netWeightKg && Number(product.netWeightKg)),
    grossWeightKg: str(product?.grossWeightKg && Number(product.grossWeightKg)),
    controlsLot: product?.controlsLot ?? false,
    strictTraceability: product?.strictTraceability ?? false,
    shelfLifeDays: str(product?.shelfLifeDays),
    status: product?.status ?? "active",
  });
  const [drafts, setDrafts] = useState<DraftPackaging[]>([]);
  const auto = useAutoCode("produto");
  const save = useSave<unknown, Product>({
    resource: "produtos",
    id: product?.id ?? null,
    invalidate: ["produtos"],
    onSaved: (p) => (product ? onClose() : onCreated(p.id)),
    successMessage: (p, created) =>
      created
        ? `Produto criado com sucesso. Código: ${p.code}${auto.note(p.code)}.`
        : "Produto atualizado com sucesso.",
  });
  const e = f.errors;
  const baseUnit = units.find((u) => u.id === (product?.baseUnitId ?? f.values.baseUnitId));

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const parsed = product
      ? productUpdateSchema.safeParse({ ...f.values, version: product.version })
      : productCreateSchema.safeParse({
          ...f.values,
          code: auto.forSave(f.values.code),
          packagings: drafts.map(draftToInput),
        });
    if (!parsed.success) return f.setErrors(issuesToErrors(parsed.error.issues));
    f.setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <Card title={product ? `${product.code} — ${product.name}` : "Novo produto"}>
      <form onSubmit={submit} className="space-y-6">
        <fieldset className="grid gap-4 sm:grid-cols-4" disabled={!canEdit}>
          <legend className="mb-2 text-sm font-semibold">Produto</legend>
          <CodeField
            auto={auto}
            value={f.values.code}
            onChange={(v) => f.set("code")(v)}
            error={e["code"]}
          />
          <div className="sm:col-span-2">
            <Field label="Nome" error={e["name"]} required>
              <Input {...f.text("name")} />
            </Field>
          </div>
          <Field label="Tipo" error={e["type"]}>
            <Select {...f.text("type")}>
              {PRODUCT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PRODUCT_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Unidade-base (estoque)"
            error={e["baseUnitId"]}
            hint={product ? "Não muda depois de criado" : "Em que unidade o estoque é contado"}
          >
            <Select {...f.text("baseUnitId")} disabled={Boolean(product)}>
              <option value="">Selecione</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} — {u.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="GTIN / EAN" error={e["gtin"]}>
            <Input inputMode="numeric" {...f.text("gtin")} />
          </Field>
          <Field label="Situação">
            <Select {...f.text("status")}>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </Select>
          </Field>
          <div className="flex items-end pb-2">
            <Checkbox label="Controla lote" {...f.check("controlsLot")} />
          </div>
          <div className="flex items-end pb-2">
            <Checkbox
              label="Rastreabilidade obrigatória (nunca vende sem estoque)"
              {...f.check("strictTraceability")}
            />
          </div>
          <Field label="Peso líquido (kg)" error={e["netWeightKg"]}>
            <Input inputMode="decimal" {...f.text("netWeightKg")} />
          </Field>
          <Field label="Peso bruto (kg)" error={e["grossWeightKg"]}>
            <Input inputMode="decimal" {...f.text("grossWeightKg")} />
          </Field>
          <Field label="Validade (dias)" error={e["shelfLifeDays"]}>
            <Input type="number" min={1} {...f.text("shelfLifeDays")} />
          </Field>
          <Field label="Descrição" error={e["description"]}>
            <Input {...f.text("description")} />
          </Field>
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-4" disabled={!canEdit}>
          <legend className="mb-2 text-sm font-semibold">Dados fiscais</legend>
          <Field label="NCM" error={e["ncm"]} hint="8 dígitos (café torrado: 0901.21.00)">
            <Input inputMode="numeric" {...f.text("ncm")} />
          </Field>
          <Field label="CEST" error={e["cest"]} hint="7 dígitos, quando houver ST">
            <Input inputMode="numeric" {...f.text("cest")} />
          </Field>
          <Field label="EX TIPI" error={e["exTipi"]} hint="Só quando o NCM tiver exceção">
            <Input inputMode="numeric" {...f.text("exTipi")} disabled={!canFiscal} />
          </Field>
          <Field
            label="Grupo tributário"
            error={e["taxGroupId"]}
            hint="Usado pelas regras fiscais; alterar exige permissão da matriz"
          >
            <Select {...f.text("taxGroupId")} disabled={!canFiscal}>
              <option value="">Sem grupo</option>
              {(groups.data ?? [])
                .filter((g) => g.status === "active" || g.id === product?.taxGroupId)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.code} — {g.name}
                  </option>
                ))}
              {product?.taxGroupId &&
                !(groups.data ?? []).some((g) => g.id === product.taxGroupId) && (
                  <option value={product.taxGroupId}>Grupo atual</option>
                )}
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Origem da mercadoria" error={e["origin"]}>
              <Select {...f.text("origin")}>
                {Object.entries(PRODUCT_ORIGINS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </fieldset>

        {!product && (
          <DraftPackagings
            units={units}
            baseUnit={baseUnit}
            drafts={drafts}
            onChange={setDrafts}
            error={Object.entries(e).find(([k]) => k.startsWith("packagings"))?.[1]}
          />
        )}

        {canEdit && (
          <div className="flex gap-2">
            <Button type="submit" disabled={save.isPending}>
              {product ? "Salvar produto" : "Criar produto"}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {product ? "Fechar" : "Cancelar"}
            </Button>
          </div>
        )}
        <SaveError error={save.error} />
      </form>

      {product && <SavedPackagings product={product} units={units} canEdit={canEdit} />}
    </Card>
  );
}

// ------------------------------------------------------------------ embalagens

function draftToInput(d: DraftPackaging) {
  const composed = d.containsUnitId !== "";
  return {
    unitId: d.unitId,
    factor: composed ? null : d.factor,
    containsQuantity: composed ? d.containsQuantity : null,
    containsUnitId: composed ? d.containsUnitId : null,
    gtin: d.gtin,
    description: d.description,
    isSaleUnit: d.isSaleUnit,
    isPurchaseUnit: d.isPurchaseUnit,
  };
}

/** Fator previsto (unidades-base por embalagem) para mostrar a conversão antes de salvar. */
function previewFactor(
  unitId: string,
  baseUnitId: string | undefined,
  list: { unitId: string; factor: string; containsQuantity: string; containsUnitId: string }[],
  depth = 0,
): string | null {
  if (unitId === baseUnitId) return "1";
  const p = list.find((x) => x.unitId === unitId);
  if (!p || depth > 10) return null;
  try {
    if (p.containsUnitId) {
      const inner = previewFactor(p.containsUnitId, baseUnitId, list, depth + 1);
      return inner === null ? null : packagingFactor(p.containsQuantity, inner);
    }
    return p.factor ? packagingFactor(p.factor, 1) : null;
  } catch {
    return null;
  }
}

function conversionText(
  unitCode: string,
  factor: string | null,
  baseCode: string | undefined,
  contains?: { quantity: string | null; unitCode: string | null },
) {
  if (factor === null || !baseCode) return "—";
  const inner =
    contains?.quantity && contains.unitCode
      ? `${formatQuantity(contains.quantity)} ${contains.unitCode} = `
      : "";
  return `1 ${unitCode} = ${inner}${formatQuantity(factor)} ${baseCode}`;
}

const emptyDraft: DraftPackaging = {
  unitId: "",
  factor: "",
  containsQuantity: "",
  containsUnitId: "",
  gtin: "",
  description: "",
  isSaleUnit: true,
  isPurchaseUnit: false,
};

function PackagingFields({
  value,
  onChange,
  units,
  baseUnit,
  others,
  lockUnit,
}: {
  value: DraftPackaging;
  onChange: (v: DraftPackaging) => void;
  units: Unit[];
  baseUnit: Unit | undefined;
  others: { unitId: string; code: string }[];
  lockUnit?: boolean;
}) {
  const set = <K extends keyof DraftPackaging>(k: K, v: DraftPackaging[K]) =>
    onChange({ ...value, [k]: v });
  const composed = value.containsUnitId !== "";
  return (
    <div className="grid gap-3 sm:grid-cols-6">
      <Field label="Unidade da embalagem">
        <Select
          value={value.unitId}
          onChange={(e) => set("unitId", e.target.value)}
          disabled={lockUnit}
        >
          <option value="">Selecione</option>
          {units
            .filter((u) => u.id !== baseUnit?.id)
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.code} — {u.name}
              </option>
            ))}
        </Select>
      </Field>
      <Field label="Como informar">
        <Select
          value={composed ? "composicao" : "fator"}
          onChange={(e) =>
            onChange(
              e.target.value === "fator"
                ? { ...value, containsUnitId: "", containsQuantity: "" }
                : { ...value, containsUnitId: baseUnit?.id ?? "", factor: "" },
            )
          }
        >
          <option value="fator">Quantidade da unidade-base</option>
          <option value="composicao">Contém outra embalagem</option>
        </Select>
      </Field>
      {composed ? (
        <>
          <Field label="Contém (quantidade)">
            <Input
              inputMode="decimal"
              value={value.containsQuantity}
              onChange={(e) => set("containsQuantity", e.target.value)}
            />
          </Field>
          <Field label="de">
            <Select
              value={value.containsUnitId}
              onChange={(e) => set("containsUnitId", e.target.value)}
            >
              {baseUnit && <option value={baseUnit.id}>{baseUnit.code} (base)</option>}
              {others
                .filter((o) => o.unitId !== value.unitId)
                .map((o) => (
                  <option key={o.unitId} value={o.unitId}>
                    {o.code}
                  </option>
                ))}
            </Select>
          </Field>
        </>
      ) : (
        <Field
          label={`Quantidade de ${baseUnit?.code ?? "base"}`}
          hint="Ex.: pacote de 500 g em KG = 0,5"
        >
          <Input
            inputMode="decimal"
            value={value.factor}
            onChange={(e) => set("factor", e.target.value)}
          />
        </Field>
      )}
      <Field label="GTIN / EAN">
        <Input
          inputMode="numeric"
          value={value.gtin}
          onChange={(e) => set("gtin", e.target.value)}
        />
      </Field>
      <div className="flex flex-col justify-end gap-1 pb-1">
        <Checkbox
          label="Venda"
          checked={value.isSaleUnit}
          onChange={(e) => set("isSaleUnit", e.target.checked)}
        />
        <Checkbox
          label="Compra"
          checked={value.isPurchaseUnit}
          onChange={(e) => set("isPurchaseUnit", e.target.checked)}
        />
      </div>
    </div>
  );
}

function DraftPackagings({
  units,
  baseUnit,
  drafts,
  onChange,
  error,
}: {
  units: Unit[];
  baseUnit: Unit | undefined;
  drafts: DraftPackaging[];
  onChange: (d: DraftPackaging[]) => void;
  error: string | undefined;
}) {
  const code = (id: string) => units.find((u) => u.id === id)?.code ?? "?";
  return (
    <fieldset className="space-y-3">
      <legend className="mb-2 text-sm font-semibold">Embalagens (opcional)</legend>
      {!baseUnit && (
        <p className="text-sm text-muted-foreground">Escolha a unidade-base primeiro.</p>
      )}
      {baseUnit &&
        drafts.map((d, i) => (
          <div key={i} className="rounded-md border p-3">
            <PackagingFields
              value={d}
              units={units}
              baseUnit={baseUnit}
              others={drafts
                .filter((_, j) => j !== i && drafts[j]?.unitId)
                .map((o) => ({ unitId: o.unitId, code: code(o.unitId) }))}
              onChange={(v) => onChange(drafts.map((x, j) => (j === i ? v : x)))}
            />
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="font-medium">
                {d.unitId
                  ? conversionText(
                      code(d.unitId),
                      previewFactor(d.unitId, baseUnit.id, drafts),
                      baseUnit.code,
                      {
                        quantity:
                          d.containsUnitId && d.containsUnitId !== baseUnit.id
                            ? d.containsQuantity
                            : null,
                        unitCode:
                          d.containsUnitId && d.containsUnitId !== baseUnit.id
                            ? code(d.containsUnitId)
                            : null,
                      },
                    )
                  : ""}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onChange(drafts.filter((_, j) => j !== i))}
              >
                Remover
              </Button>
            </div>
          </div>
        ))}
      {baseUnit && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onChange([...drafts, { ...emptyDraft }])}
        >
          Adicionar embalagem
        </Button>
      )}
      {error && <Alert tone="error">{error}</Alert>}
    </fieldset>
  );
}

function SavedPackagings({
  product,
  units,
  canEdit,
}: {
  product: Product;
  units: Unit[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const packagings = product.packagings ?? [];
  const baseUnit = units.find((u) => u.id === product.baseUnitId);
  const [editing, setEditing] = useState<Packaging | "new" | null>(null);
  const [draft, setDraft] = useState<DraftPackaging>(emptyDraft);
  const [key, setKey] = useState(newIdempotencyKey);
  const [formError, setFormError] = useState<string | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["cadastros", "produtos"] });
    await queryClient.invalidateQueries({ queryKey: ["cadastros", "tabelas-preco"] });
  };

  const saveMut = useMutation({
    mutationFn: (body: unknown) =>
      editing && editing !== "new"
        ? apiFetch(`/cadastros/produtos/${product.id}/embalagens/${editing.id}`, {
            method: "PUT",
            body,
          })
        : apiFetch(`/cadastros/produtos/${product.id}/embalagens`, {
            method: "POST",
            body,
            idempotencyKey: key,
          }),
    onSuccess: async () => {
      const created = editing === "new";
      setEditing(null);
      setKey(newIdempotencyKey());
      await refresh();
      notifySuccess(
        created ? "Embalagem criada com sucesso." : "Embalagem atualizada com sucesso.",
      );
    },
  });
  const toggleMut = useMutation({
    mutationFn: (p: Packaging) =>
      apiFetch(`/cadastros/produtos/${product.id}/embalagens/${p.id}`, {
        method: "PUT",
        body: {
          unitId: p.unitId,
          factor: p.containsUnitId ? null : p.factor,
          containsQuantity: p.containsQuantity,
          containsUnitId: p.containsUnitId,
          gtin: p.gtin,
          description: p.description,
          isSaleUnit: p.isSaleUnit,
          isPurchaseUnit: p.isPurchaseUnit,
          status: p.status === "active" ? "inactive" : "active",
          version: p.version,
        },
      }),
    onSuccess: refresh,
  });
  const removeMut = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/cadastros/produtos/${product.id}/embalagens/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await refresh();
      notifySuccess("Embalagem removida.");
    },
  });

  function open(p: Packaging | "new") {
    setFormError(null);
    saveMut.reset();
    setEditing(p);
    setDraft(
      p === "new"
        ? { ...emptyDraft }
        : {
            unitId: p.unitId,
            factor: p.containsUnitId ? "" : formatQuantity(p.factor),
            containsQuantity: p.containsQuantity ? formatQuantity(p.containsQuantity) : "",
            containsUnitId: p.containsUnitId ?? "",
            gtin: p.gtin ?? "",
            description: p.description ?? "",
            isSaleUnit: p.isSaleUnit,
            isPurchaseUnit: p.isPurchaseUnit,
          },
    );
  }

  function submit() {
    const body = {
      ...draftToInput(draft),
      status: editing && editing !== "new" ? editing.status : "active",
    };
    const parsed = packagingInputSchema.safeParse(body);
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    saveMut.mutate(
      editing && editing !== "new" ? { ...parsed.data, version: editing.version } : parsed.data,
    );
  }

  return (
    <div className="mt-8 space-y-3 border-t pt-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Embalagens e conversões</h3>
        {canEdit && editing === null && (
          <Button size="sm" variant="secondary" onClick={() => open("new")}>
            Nova embalagem
          </Button>
        )}
      </div>
      <Table head={["Unidade", "Conversão", "GTIN", "Uso", "Situação", ""]}>
        <tr>
          <td className="px-3 py-2 font-mono">{baseUnit?.code}</td>
          <td className="px-3 py-2 text-muted-foreground">unidade-base do estoque</td>
          <td className="px-3 py-2">{product.gtin ?? "—"}</td>
          <td className="px-3 py-2" />
          <td className="px-3 py-2" />
          <td />
        </tr>
        {packagings.map((p) => (
          <tr key={p.id}>
            <td className="px-3 py-2 font-mono">{p.unitCode}</td>
            <td className="px-3 py-2 font-medium">
              {conversionText(p.unitCode, p.factor, baseUnit?.code, {
                quantity:
                  p.containsUnitId && p.containsUnitId !== product.baseUnitId
                    ? p.containsQuantity
                    : null,
                unitCode:
                  p.containsUnitId && p.containsUnitId !== product.baseUnitId
                    ? p.containsUnitCode
                    : null,
              })}
            </td>
            <td className="px-3 py-2">{p.gtin ?? "—"}</td>
            <td className="px-3 py-2 text-xs">
              {[p.isSaleUnit && "venda", p.isPurchaseUnit && "compra"].filter(Boolean).join(", ") ||
                "—"}
            </td>
            <td className="px-3 py-2">
              <StatusBadge status={p.status} />
            </td>
            <td className="space-x-1 px-3 py-2 text-right whitespace-nowrap">
              {canEdit && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => open(p)}>
                    Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleMut.mutate(p)}>
                    {p.status === "active" ? "Inativar" : "Reativar"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeMut.mutate(p.id)}>
                    Excluir
                  </Button>
                </>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {editing !== null && (
        <div className="rounded-md border p-3">
          <PackagingFields
            value={draft}
            onChange={setDraft}
            units={units}
            baseUnit={baseUnit}
            lockUnit={editing !== "new"}
            others={packagings.map((p) => ({ unitId: p.unitId, code: p.unitCode }))}
          />
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={submit} disabled={saveMut.isPending}>
              Salvar embalagem
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
      {formError && <Alert tone="error">{formError}</Alert>}
      <SaveError error={saveMut.error ?? toggleMut.error ?? removeMut.error} />
    </div>
  );
}
